import { App } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useCtxGroupCompany } from '../../companyGroup/queries/useCtxGroupCompany'
import { revokeReconciliationApprovals } from '../service/reconciliationService'
import { Match } from '../types'
import reconciliationQueryKeys from './reconciliationQueryKeys'

// Accepts the Matches whose acceptance should be revoked; pulls their approvalIds.
// `not_found` is treated as success — the acceptance is gone either way.
export const useRevokeApprovals = () => {
  const { data: groupCompany } = useCtxGroupCompany()
  const groupCompanyId = groupCompany?.id
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (matches: Match[]) => {
      if (!groupCompanyId) throw new Error('Group company id is required')
      const approvalIds = matches
        .map(match => match.accepted?.approvalId)
        .filter((id): id is number => typeof id === 'number')
      if (approvalIds.length === 0) throw new Error('No approvals to revoke')
      return revokeReconciliationApprovals(groupCompanyId, approvalIds)
    },
    onSuccess: (response, matches) => {
      // Drop the acceptance from the list cache instead of refetching the whole dataset.
      if (groupCompanyId) {
        const revokedIds = new Set(
          matches.map(match => match.accepted?.approvalId).filter((id): id is number => typeof id === 'number')
        )
        queryClient.setQueryData<Match[]>(reconciliationQueryKeys.list({ groupCompanyId }), current =>
          current?.map(match =>
            match.accepted && revokedIds.has(match.accepted.approvalId)
              ? { ...match, accepted: undefined, acceptanceStale: false }
              : match
          )
        )
      }

      message.success(
        t('icReconciliation:revokeResult', '{{count}} hyväksyntää peruttu', { count: response.results.length })
      )
      queryClient.invalidateQueries({ queryKey: reconciliationQueryKeys.lists(), refetchType: 'none' })
    },
    onError: () => {
      message.error(t('icReconciliation:revokeError', 'Hyväksynnän peruminen epäonnistui'))
    }
  })
}
